import React, { createContext, useEffect, useReducer, useRef } from "react";
import { history } from "src/App";
import SplashScreen from "src/components/SplashScreen";

import {
  AccountInfo,
  AuthenticationResult,
  Configuration,
  PublicClientApplication,
  RedirectRequest,
  SilentRequest,
} from "@azure/msal-browser";
import { BrowserAuthOptions } from "@azure/msal-browser/dist/src/config/Configuration";
import { ServerError } from "@azure/msal-common";
import { isFuture, sub } from "date-fns";
import { authConfig } from "src/config";
import { User } from "src/types/user";

// Types

interface LoginRequest {
  state?: string;
  account?: AccountInfo;
}

export interface AuthState {
  isInitialised: boolean;
  isAuthenticated: boolean;
  activeAccount: null | AuthenticationResult;
  user?: User;
  isActiveAccountsTokenValid: boolean;
  authResults?: AuthenticationResult[];
  msalAuthAgent?: PublicClientApplication;
}

interface AuthContextValue extends AuthState {
  login: (req: LoginRequest) => Promise<void>;
  logout: () => void;
  register: () => Promise<void>;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

type InitialiseAction = {
  type: "INITIALISE";
  payload: { msalAuthAgent: PublicClientApplication };
};

type LoginAction = {
  type: "LOGIN";
  payload: {
    user: User;
    activeAccount: AuthenticationResult;
    isActiveAccountsTokenValid: boolean;
    authResults?: AuthenticationResult[];
    msalAuthAgent?: PublicClientApplication;
  };
};

type LogoutAction = {
  type: "LOGOUT";
};

type RenewAction = {
  type: "RENEW";
  payload: {
    activeAccount?: AuthenticationResult;
    user?: User;
    isActiveAccountsTokenValid?: boolean;
  };
};

type RegisterAction = {
  type: "REGISTER";
  payload: {
    user: User;
    activeAccount: AuthenticationResult;
  };
};

type Action =
  | InitialiseAction
  | LoginAction
  | LogoutAction
  | RegisterAction
  | RenewAction;

// Config
const {
  useApiScope,
  authority,
  redirectUri,
  resetAuthority,
  clientId,
  logoutRedirectUri,
  FORGOT_PW_ERROR_CODE,
  FORGOT_PW_ERROR_INDICATOR,
} = authConfig;

const scopes: string[] = [useApiScope];

const authenticationParameters: BrowserAuthOptions = {
  authority,
  knownAuthorities: [authority, resetAuthority],
  clientId,
  redirectUri: redirectUri(),
  navigateToLoginRequestUrl: false,
  postLogoutRedirectUri: logoutRedirectUri(),
};

const config: Configuration = {
  auth: authenticationParameters,
  cache: {
    cacheLocation: "localStorage",
    storeAuthStateInCookie: false,
  },
};

const initialAuthState: AuthState = {
  isAuthenticated: false,
  isInitialised: false,
  activeAccount: null,
  isActiveAccountsTokenValid: false,
};

const delayWhenNothingChanged = 1 * 60 * 1000;
const delayWhenStateChanged = 10000;

// Methods

const redirectRequest = (
  account?: AccountInfo,
  state?: string
): RedirectRequest => ({ ...authenticationParameters, scopes, account, state });

const acquireTokenSilentRequest = (account: AccountInfo): SilentRequest => ({
  account,
  scopes,
  authority,
  forceRefresh: false,
});

const acquireTokenSilent = async (
  msalAuthAgent: PublicClientApplication,
  account: AccountInfo
) => {
  try {
    const req: SilentRequest = acquireTokenSilentRequest(account);
    const result: AuthenticationResult = await msalAuthAgent.acquireTokenSilent(
      req
    );
    return result;
  } catch (error) {
    return undefined;
  }
};

const renewToken = async (
  msalAuthAgent?: PublicClientApplication,
  activeAccount?: AuthenticationResult | null,
  isActiveAccountsTokenValid?: boolean
): Promise<{
  delay: number;
  activeAccount?: AuthenticationResult;
  isActiveAccountsTokenValid?: boolean;
}> => {
  if (activeAccount && msalAuthAgent) {
    return await renewTokenForAccount(
      msalAuthAgent,
      activeAccount,
      isActiveAccountsTokenValid
    );
  } else if (msalAuthAgent && !activeAccount && isActiveAccountsTokenValid) {
    return { delay: delayWhenStateChanged, isActiveAccountsTokenValid: false };
  }
  return { delay: delayWhenNothingChanged };
};

const renewTokenForAccount = async (
  authAgent: PublicClientApplication,
  activeAccount: AuthenticationResult,
  isActiveAccountsTokenValid?: boolean
) => {
  const tokenValid = isAccessTokenValid(activeAccount);
  if (tokenValid === isActiveAccountsTokenValid) {
    // Token and state are synchronized
    return {
      delay: delayWhenNothingChanged,
      isActiveAccountsTokenValid: tokenValid,
    };
  } else if (tokenValid) {
    // Token is valid but out of sync.
    // Recheck in 10 seconds.
    return {
      delay: delayWhenStateChanged,
      isActiveAccountsTokenValid: tokenValid,
    };
  } else {
    // Token is invalid and out of sync.
    // Try to renew the token and update the state.
    const authResult = await acquireTokenSilent(
      authAgent,
      activeAccount.account
    );
    if (authResult) {
      return {
        activeAccount: authResult,
        isActiveAccountsTokenValid: isAccessTokenValid(authResult),
        delay: delayWhenStateChanged,
      };
    }
    // If renewing of the token failed, adjust the state
    return {
      isActiveAccountsTokenValid: tokenValid,
      delay: delayWhenStateChanged,
    };
  }
};

const forgotPasswordRedirectRequest = (
  account?: AccountInfo,
  state?: string
): RedirectRequest => ({
  ...redirectRequest(account, state),
  authority: resetAuthority,
});

const isAccessTokenValid = (authResult: AuthenticationResult): boolean => {
  return Boolean(
    authResult?.accessToken &&
      authResult?.expiresOn &&
      isFuture(sub(authResult?.expiresOn, { minutes: 4 }))
  );
};

const processAccounts = (
  authResults: AuthenticationResult[]
): Partial<AuthState> => {
  const accountsWithValidAccessToken = authResults.filter(isAccessTokenValid);
  const activeAccount =
    accountsWithValidAccessToken.length > 0
      ? accountsWithValidAccessToken[0]
      : undefined;

  return {
    authResults,
    activeAccount,

    isActiveAccountsTokenValid: activeAccount
      ? isAccessTokenValid(activeAccount)
      : false,
  };
};

const convertAuthResultToUser = (authResult: AuthenticationResult): User => {
  return {
    email: authResult.account.username,
    //@ts-ignore
    name: `${authResult?.idTokenClaims?.given_name} ${authResult?.idTokenClaims?.family_name}`,
  };
};

const reducer = (state: AuthState, action: Action): AuthState => {
  switch (action.type) {
    case "INITIALISE": {
      return {
        ...state,
        msalAuthAgent: action.payload.msalAuthAgent,
        isInitialised: true,
      };
    }
    case "RENEW": {
      return {
        ...state,
        ...action.payload,
      };
    }
    case "LOGIN": {
      return {
        ...state,
        isAuthenticated: true,
        ...action.payload,
      };
    }
    case "LOGOUT": {
      return {
        ...state,
        isAuthenticated: false,
        isInitialised: false,
        user: undefined,
        activeAccount: null,
        isActiveAccountsTokenValid: false,
      };
    }
    case "REGISTER": {
      const { user, activeAccount } = action.payload;

      return {
        ...state,
        isAuthenticated: true,
        user,
        activeAccount,
      };
    }
    default: {
      return { ...state };
    }
  }
};

const AuthContext = createContext<AuthContextValue>({
  ...initialAuthState,
  login: (req: LoginRequest) => Promise.resolve(),
  logout: () => {},
  register: () => Promise.resolve(),
});

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialAuthState);

  // Pointer to ref for renewel timeout function.
  const ref = useRef(state);
  ref.current = state;

  const login = async (req: LoginRequest) => {
    const msalAuthAgent = state.msalAuthAgent;
    if (!msalAuthAgent) return;

    if (req.account) {
      const authResult:
        | AuthenticationResult
        | undefined = await acquireTokenSilent(msalAuthAgent, req.account);
      if (authResult && isAccessTokenValid(authResult)) {
        dispatch({
          type: "LOGIN",
          payload: {
            activeAccount: authResult,
            user: convertAuthResultToUser(authResult),
            isActiveAccountsTokenValid: isAccessTokenValid(authResult),
          },
        });
      }

      await msalAuthAgent.acquireTokenRedirect(
        redirectRequest(req.account, req.state)
      );
    } else {
      await msalAuthAgent.loginRedirect(
        redirectRequest(req.account, req.state)
      );
    }
  };

  const logout = () => {
    localStorage.clear();
    state.msalAuthAgent?.logout();
    dispatch({ type: "LOGOUT" });
  };

  const register = async () => {
    // REGISTER
  };

  // Initialise SDK
  useEffect(() => {
    const initialise = async () => {
      const msalAuthAgent = new PublicClientApplication(config);

      // Get available accounts
      const accounts = msalAuthAgent.getAllAccounts();
      // Check if we're coming from a login redirect
      try {
        const authResult: AuthenticationResult | null = await msalAuthAgent.handleRedirectPromise();
        if (authResult) {
          const processedAccounts = processAccounts([authResult]);
          const activeAccount = processedAccounts.activeAccount;
          if (!activeAccount) throw new Error();
          dispatch({
            type: "LOGIN",
            payload: {
              activeAccount,
              user: convertAuthResultToUser(authResult),
              isActiveAccountsTokenValid: processedAccounts.isActiveAccountsTokenValid!,
              authResults: processedAccounts.authResults!,
            },
          });

          if (
            authResult.state &&
            (authResult.state.startsWith(redirectUri()) ||
              authResult.state.startsWith("/"))
          ) {
            history.push(authResult.state);
          }
          dispatch({ type: "INITIALISE", payload: { msalAuthAgent } });
          return;
        }
      } catch (error) {
        const err = error as ServerError;
        if (err) {
          if (
            err.errorCode === FORGOT_PW_ERROR_CODE &&
            (err.errorMessage?.startsWith(FORGOT_PW_ERROR_INDICATOR) ?? false)
          ) {
            const acquireConfig = forgotPasswordRedirectRequest(
              undefined,
              window.location.pathname
            );
            await msalAuthAgent.loginRedirect(acquireConfig);
          }
        }
        dispatch({ type: "INITIALISE", payload: { msalAuthAgent } });
        return;
      }

      const maybeAccountAuthResults: (
        | AuthenticationResult
        | undefined
      )[] = await Promise.all(
        accounts.map((account) => acquireTokenSilent(msalAuthAgent, account))
      );
      const authResults = maybeAccountAuthResults.reduce<
        AuthenticationResult[]
      >((acc, it) => (it == null ? acc : acc.concat([it])), []);

      const processedAccounts = processAccounts(authResults);
      const activeAccount = processedAccounts.activeAccount;
      if (activeAccount) {
        dispatch({
          type: "LOGIN",
          payload: {
            activeAccount,
            user: convertAuthResultToUser(activeAccount),
            isActiveAccountsTokenValid: processedAccounts.isActiveAccountsTokenValid!,
            authResults: processedAccounts.authResults!,
          },
        });
      }
      dispatch({ type: "INITIALISE", payload: { msalAuthAgent } });
    };
    initialise();
  }, []);

  // Renew Token
  useEffect(() => {
    if (state.msalAuthAgent) {
      let timeout: NodeJS.Timeout;
      const renewTokenTimeout = async () => {
        const res = await renewToken(
          ref.current.msalAuthAgent,
          ref.current.activeAccount,
          ref.current.isActiveAccountsTokenValid
        );
        const { activeAccount, isActiveAccountsTokenValid, delay } = res;
        // If we got updated data, update in state
        if (activeAccount) {
          dispatch({
            type: "RENEW",
            payload: {
              activeAccount,
              user: convertAuthResultToUser(activeAccount),
              isActiveAccountsTokenValid,
            },
          });
        } else {
          dispatch({
            type: "RENEW",
            payload: {
              isActiveAccountsTokenValid,
            },
          });
        }
        // Schedule the next timeout
        timeout = setTimeout(renewTokenTimeout, delay);
      };
      timeout = setTimeout(renewTokenTimeout, delayWhenNothingChanged);
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [state.msalAuthAgent]);

  if (!state.isInitialised) {
    return <SplashScreen />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        register,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
